import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { HeaderStickyBanner } from "@/components/AdPlacements";
import SEO from "@/components/SEO";
import { Clock, Newspaper, TrendUp } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function UKNewsPage() {
  const [articles, setArticles] = useState([]);
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const fetchArticles = useCallback(async (pageNum = 1) => {
    try {
      const res = await axios.get(`${API}/api/articles`, {
        params: { region: "uk", limit: 20, page: pageNum }
      });
      // API returns array directly
      const articles = Array.isArray(res.data) ? res.data : (res.data.articles || []);
      if (pageNum === 1) {
        setArticles(articles);
      } else {
        setArticles(prev => [...prev, ...articles]);
      }
      setHasMore(articles.length === 20);
      setPage(pageNum);
    } catch (err) {
      console.error("Error fetching articles:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFeatured = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/articles/featured`, {
        params: { region: "uk", limit: 3 }
      });
      setFeaturedArticles(res.data.articles || []);
    } catch (err) {
      console.error("Error fetching featured:", err);
    }
  }, []);

  useEffect(() => {
    fetchArticles();
    fetchFeatured();
  }, [fetchArticles, fetchFeatured]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
    if (diffHours < 1) return "Just now";
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };

  return (
    <div className="min-h-screen bg-[#FFF1E5]">
      <SEO title="UK Farming News" description="Latest UK farming news, agricultural policy, livestock markets and arable updates from England, Scotland, Wales and Northern Ireland." url="/uk-news" />
      <HeaderStickyBanner />
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#022C22] to-[#064E3B] py-8">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-2">
            <span className="px-3 py-1 bg-[#059669] text-white text-xs font-mono uppercase tracking-wider">
              United Kingdom
            </span>
            <span className="px-3 py-1 bg-[#FBBF24] text-[#022C22] text-xs font-mono uppercase tracking-wider">
              Farming News
            </span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl text-white mb-2">
            UK Farming News
          </h1>
          <p className="text-[#A7F3D0] text-lg">
            Latest agricultural news, market updates, and farming insights from the United Kingdom
          </p>
        </div>
      </section>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Featured Analysis */}
        {featuredArticles.length > 0 && (
          <section className="mb-12">
            <div className="flex items-center gap-2 mb-6">
              <TrendUp size={24} className="text-[#0F766E]" />
              <h2 className="font-serif text-2xl text-[#33302E]">Featured Analysis</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredArticles.map((article, index) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-[#022C22] p-6 hover:bg-[#064E3B] transition-colors"
                >
                  <span className="inline-block px-2 py-0.5 bg-[#5EEAD4]/20 text-[#5EEAD4] text-[10px] font-mono uppercase tracking-wider mb-3">
                    {article.category || "Analysis"}
                  </span>
                  <h3 className="font-serif text-lg text-white mb-3 line-clamp-2 group-hover:text-[#5EEAD4] transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-[#A7F3D0] text-sm line-clamp-3 mb-4">
                    {article.summary}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-[#6EE7B7]">
                    <Clock size={12} />
                    <span>{formatDate(article.published_at)}</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* All News */}
        <section>
          <div className="flex items-center gap-2 mb-6">
            <Newspaper size={24} className="text-[#0F766E]" />
            <h2 className="font-serif text-2xl text-[#33302E]">All UK News</h2>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F766E]"></div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {articles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-white border border-[#E0D4C8] p-5 hover:border-[#0F766E] transition-colors"
                  >
                    <span className="inline-block px-2 py-0.5 bg-[#0F766E] text-white text-[10px] font-mono uppercase tracking-wider mb-2">
                      {article.category || "News"}
                    </span>
                    <h3 className="font-serif text-lg text-[#33302E] mb-2 line-clamp-2 group-hover:text-[#0F766E] transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-[#6B6661] text-sm line-clamp-3 mb-3">
                      {article.summary}
                    </p>
                    <div className="flex items-center justify-between text-xs text-[#6B6661]">
                      <div className="flex items-center gap-2">
                        <Clock size={12} />
                        <span>{formatDate(article.published_at)}</span>
                      </div>
                      <span className="truncate max-w-[120px]">{article.source}</span>
                    </div>
                  </Link>
                ))}
              </div>

              {hasMore && (
                <div className="text-center mt-8">
                  <button
                    onClick={() => fetchArticles(page + 1)}
                    className="px-6 py-3 bg-[#0F766E] text-white font-medium hover:bg-[#064E3B] transition-colors"
                  >
                    Load More News
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}
